import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, IconButton, useTheme, TextField, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, useMediaQuery, DialogContentText } from '@mui/material';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { tokens } from '../../../theme';
import { addUser, deleteUser, getUsers, editUser, currentUser } from '../../../redux/slice/users.slice';
import Header from "../../../components/Header";
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import { getRoles } from '../../../redux/slice/roles.slice';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import DownloadIcon from '@mui/icons-material/Download';
import { Image_URL } from '../../../utils/baseURL';


const UserDataGrid = ({ data, columns }) => {
    if (!data || !Array.isArray(data)) {
        return <div>No data availble...</div>;
    }
    return (
        <DataGrid
            rows={data}
            columns={columns}
            components={{ Toolbar: GridToolbar }}
            pageSize={5}
            rowsPerPageOptions={[5, 10]}
            density="compact"
            getRowId={(row) => row?._id}
            autoHeight
            sx={{
                "@media print": {
                    "& .MuiDataGrid-toolbarContainer": {
                        display: "none !important"
                    },
                    "& .MuiDataGrid-root": {
                        color: "black !important",
                    },
                    "& .MuiDataGrid-cell": {
                        color: "black !important",
                    },
                    "& .MuiDataGrid-columnHeaders": {
                        color: "black !important",
                    },
                    "& .MuiDataGrid-footerContainer": {
                        color: "black !important",
                    },
                    "& .css-7ms3qr-MuiTablePagination-displayedRows": {
                        color: "black !important",
                    },
                },
                '& .MuiDataGrid-cell': {
                    py: '8px',
                },

            }}
        />
    );
};

const User = () => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const colors = tokens(theme.palette.mode);
    const imgUrl = Image_URL;
    const isMobile = useMediaQuery('(max-width:500px)');
    const [open, setOpen] = useState(false);
    const [update, setUpdate] = useState(null);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [deleteUserData, setDeleteUserData] = useState(null);
    const hasFetchedData = useRef(false);
    const users = useSelector(state => state.users.allusers);
    const roles = useSelector(state => state.roles.roles);
    const [usersWithRoles, setUsersWithRoles] = useState([]);

    useEffect(() => {
        dispatch(getUsers());
        dispatch(getRoles());
    }, [dispatch]);

    useEffect(() => {
        if (users && roles) {
            const updatedUsers = users.map(user => {
                const role = roles?.find(r => r._id === user.role);
                return {
                    ...user,
                    roleData: role ? [{ roleName: role.roleName, permissions: role.permissions }] : []
                };
            });
            setUsersWithRoles(updatedUsers);
        }
    }, [users, roles]);

    const handleClickOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        formik.resetForm();
        setUpdate(null);
        setAvatarError('');
    };

    const handleDelete = (id) => {
        setDeleteUserData(id);
        setDeleteConfirmOpen(true);
    };

    const confirmDelete = () => {
        if (deleteUserData) {
            dispatch(deleteUser(deleteUserData));
            setDeleteConfirmOpen(false);
            setDeleteUserData(null);
        }
    }
    const cancelDelete = () => {
        setDeleteConfirmOpen(false);
        setDeleteUserData(null);
    }

    const handleEdit = (data) => {
        formik.setValues(data);
        setOpen(true);
        setUpdate(data._id);

    };

    const columns = useMemo(() => [
        {
            field: 'Sr no',
            headerName: 'Sr No',
            width: 50,
            valueGetter: (params) => {
                const index = params.api.getAllRowIds().indexOf(params.row._id);
                return index + 1;
            }
        },
        {
            field: 'avatar',
            headerName: 'Avatar',
            width: 150,
            renderCell: (params) => {
                const avatarUrl = `${imgUrl}/${params.row.avatar}`;
                return (
                    <img
                        style={{ width: '50px', height: '50px', objectFit: 'cover', objectPosition: 'center center' }}
                        src={params.row.avatar ? avatarUrl : ''}
                        alt="avatar"
                        onError={(e) => { e.target.onerror = null; e.target.src = ''; }}
                    />
                );
            }
        },
        { field: 'name', headerName: 'Name', width: 200 },
        { field: 'email', headerName: 'Email', width: 230 },
        { field: 'contact', headerName: 'Mobile No', width: 150 },
        { field: 'address', headerName: 'Address', width: 150 },
        {
            field: 'role', headerName: 'Role', width: 230,
            renderCell: (params) => {
                return params.row.roleData?.[0]?.roleName;
            }
        },
        {
            field: 'permissions',
            headerName: 'Permissions',
            width: 600,
            renderCell: (params) => (
                <div style={{
                    width: '100%',
                    padding: '8px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                }}>
                    {params.row.roleData?.[0]?.permissions ? params.row.roleData?.[0]?.permissions?.join(' , ') : 'No permissions available'}
                </div>
            )
        },
        {
            field: 'Action',
            headerName: 'Action',
            width: 130,
            renderCell: (params) => (
                <>
                    <IconButton aria-label="view" onClick={() => handleViewData(params.row)}>
                        <RemoveRedEyeIcon />
                    </IconButton>
                    <IconButton aria-label="edit" onClick={() => handleEdit(params.row)}>
                        <EditIcon />
                    </IconButton>
                    <IconButton aria-label="delete" onClick={() => handleDelete(params.row._id)}>
                        <DeleteIcon />
                    </IconButton>
                </>
            )
        }
    ], []);

    const userSchema = yup.object({
        name: yup.string().required("Please enter user name"),
        email: yup.string().email("Enter a valid email").required("Please enter user email"),
        contact: yup.string().required("Please enter user mobile number"),
        address: yup.string().required("Please enter user address"),
        password: yup.string().required("Please enter a password"),
        role: yup.string().required("Please select a role"),
        avatar: yup.mixed()
            .required("Please select an image")
            .test("fileSize", "The file is too large", (value) => {
                if (value && value.size) {
                    return value.size <= 2 * 1024 * 1024;
                }
                return true;
            })
            .test("fileType", "Unsupported File Format", (value) => {
                if (value && value.type) {
                    return ["image/jpg", "image/jpeg", "image/png", "image/gif", "image/webp"].includes(value.type);
                }
                return true;
            }),
    });

    const formik = useFormik({
        initialValues: {
            name: '',
            email: '',
            contact: '',
            address: '',
            password: '',
            role: '',
            avatar: ''
        },
        validationSchema: userSchema,

        onSubmit: (values, { resetForm }) => {
            const role = roles?.find(r => r._id === values.role);
            const userData = {
                ...values,
                roleData: role ? [{ roleName: role.roleName, permissions: role.permissions }] : []
            };

            if (update) {
                dispatch(editUser({
                    _id: update,
                    ...userData
                }));
                dispatch(currentUser());
            } else {
                dispatch(addUser(userData));
            }
            resetForm();
            handleClose();
        }
    });

    const { handleSubmit, handleChange, handleBlur, values, errors, touched, setFieldValue } = formik;

    const [openView, setOpenView] = React.useState(false);
    const [viewImage, setViewImage] = useState(null);
    const downloadRef = React.useRef(null);
    const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
    const [openView2, setOpenView2] = React.useState(false);
    const [viewData, setViewData] = useState(null);

    const handleView = (imageType) => {
        let imageUrl;
        const baseUrl = imgUrl;

        if (imageType === 'avatar') {
            imageUrl = values.avatar instanceof File ? URL.createObjectURL(values.avatar) :
                (values.avatar ? `${baseUrl}/${values.avatar}` : '');
        }

        console.log("Image URL in handleView:", imageUrl);
        setViewImage(imageUrl);
        setOpenView(true);
    }


    const handleClickOpenView = () => {
        setOpenView2(true);
    };

    const handleViewData = (data) => {
        handleClickOpenView();
        setViewData(data)
    }


    const handleCloseView = () => {
        setOpenView(false);
    };
    const handleCloseView2 = () => {
        setOpenView2(false);
    };

    const handleDownload = async () => {
        if (viewImage) {
            try {
                const proxyUrl = `/api/proxy-image?url=${encodeURIComponent(viewImage)}`;
                const response = await fetch(proxyUrl);

                console.log('Response status:', response.status);

                if (response.ok) {
                    const blob = await response.blob();
                    const contentType = response.headers.get('Content-Type');
                    const extension = contentType.split('/')[1];
                    const url = URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = `downloaded-image.${extension}`;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    URL.revokeObjectURL(url);
                } else {
                    console.error('Failed to download image, status:', response.status);
                }
            } catch (error) {
                console.error('Error in download process:', error);
            }
        } else {
            console.log("No image available");
        }
    };

    const [showPassword, setShowPassword] = useState(false);
    const [avatarError, setAvatarError] = useState('');

    return (
        <Box m="2px">
            <Header
                title="USERS"
                subtitle="Manage Your Users Here"
            />
            <Box display="flex" justifyContent="flex-end" mb={2}>
                <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={handleClickOpen}>
                    Add User
                </Button>
            </Box>

            <Dialog open={open} onClose={handleClose}>
                <DialogTitle>{update ? 'Edit User' : 'Add User'}</DialogTitle>
                <form onSubmit={handleSubmit} encType="multipart/form-data">
                    <DialogContent>
                        <TextField
                            margin="dense"
                            id="name"
                            name="name"
                            label="Enter User Name"
                            type="text"
                            fullWidth
                            variant="filled"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.name}
                            error={touched.name && Boolean(errors.name)}
                            helperText={touched.name && errors.name}
                            InputLabelProps={{
                                style: {
                                    color: theme.palette.text.primary
                                }
                            }}
                        />
                        <TextField
                            margin="dense"
                            id="email"
                            name="email"
                            label="Enter User Email"
                            type="email"
                            fullWidth
                            variant="filled"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.email}
                            error={touched.email && Boolean(errors.email)}
                            helperText={touched.email && errors.email}
                            InputLabelProps={{
                                style: {
                                    color: theme.palette.text.primary
                                }
                            }}
                        />
                        <TextField
                            margin="dense"
                            id="contact"
                            name="contact"
                            label="Enter User Mobile Number"
                            type="text"
                            fullWidth
                            variant="filled"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.contact}
                            error={touched.contact && Boolean(errors.contact)}
                            helperText={touched.contact && errors.contact}
                            InputLabelProps={{
                                style: {
                                    color: theme.palette.text.primary
                                }
                            }}
                        />
                        <TextField
                            margin="dense"
                            id="address"
                            name="address"
                            label="Enter User Address"
                            type="text"
                            fullWidth
                            variant="filled"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.address}
                            error={touched.address && Boolean(errors.address)}
                            helperText={touched.address && errors.address}
                            InputLabelProps={{
                                style: {
                                    color: theme.palette.text.primary
                                }
                            }}
                        />
                        <TextField
                            margin="dense"
                            id="password"
                            name="password"
                            label="Enter Password"
                            type={showPassword ? "text" : "password"}
                            fullWidth
                            variant="filled"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.password}
                            error={touched.password && Boolean(errors.password)}
                            helperText={touched.password && errors.password}
                            InputLabelProps={{
                                style: {
                                    color: theme.palette.text.primary
                                }
                            }}
                            InputProps={{
                                endAdornment: (
                                    <IconButton
                                        onClick={() => setShowPassword(!showPassword)}
                                        edge="end"
                                    >
                                        {showPassword ? <RemoveRedEyeIcon /> : <RemoveRedEyeIcon />}
                                    </IconButton>
                                ),
                            }}
                        />
                        <TextField
                            margin="dense"
                            id="role"
                            name="role"
                            label="Select Role"
                            select
                            fullWidth
                            variant="filled"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.role}
                            error={touched.role && Boolean(errors.role)}
                            helperText={touched.role && errors.role}
                            InputLabelProps={{
                                style: {
                                    color: theme.palette.text.primary
                                }
                            }}
                        >
                            {
                                roles?.map((v) => (
                                    <MenuItem value={v._id}>{v.roleName}</MenuItem>
                                ))
                            }
                        </TextField>
                        <br></br>
                        <div style={{ display: 'flex', alignItems: 'center', marginTop: '20px' }}>
                            <Button
                                variant="contained"
                                component="label"
                                style={{ marginRight: '10px' }}
                            >
                                Upload Avatar
                                <input
                                    type="file"
                                    hidden
                                    id="avatar"
                                    name="avatar"
                                    onChange={(event) => {
                                        const file = event.currentTarget.files[0];
                                        if (file) {
                                            if (file.size > 2 * 1024 * 1024) {
                                                setAvatarError("File size exceeds 2 MB");
                                                setFieldValue("avatar", null);
                                            } else {
                                                setAvatarError('');
                                                setFieldValue("avatar", file);
                                            }
                                        }
                                    }}
                                    onBlur={handleBlur}
                                />
                            </Button>
                            {avatarError && <span style={{ color: "red" }}>{avatarError}</span>}
                            {values.avatar && (
                                <img
                                    src={typeof values.avatar === 'string' ? `${imgUrl}/` + values.avatar : URL.createObjectURL(values.avatar)}
                                    alt="avatar"
                                    width="50"
                                    height="50"
                                    style={{ objectFit: 'cover', borderRadius: '5px', marginLeft: '20px' }}
                                />
                            )}
                            <IconButton aria-label="edit" onClick={() => handleView('avatar')}>
                                <RemoveRedEyeIcon />
                            </IconButton>
                        </div>
                        {errors.avatar && touched.avatar ? <span style={{ color: "red" }}>{errors.avatar}</span> : null}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleClose} style={{ color: theme.palette.text.primary }}>Cancel</Button>
                        <Button type="submit" style={{ color: theme.palette.text.primary }}>{update ? 'Update' : 'Add'}</Button>
                    </DialogActions>
                </form>
            </Dialog>

            <Box
                height="75vh"
                sx={useMemo(() => ({
                    "& .MuiDataGrid-root": {
                        border: "none",
                    },
                    "& .MuiDataGrid-columnHeaders": {
                        backgroundColor: colors.blueAccent[700],
                        borderBottom: "none",
                    },
                    "& .MuiDataGrid-virtualScroller": {
                        backgroundColor: colors.primary[400],
                    },
                    "& .MuiDataGrid-footerContainer": {
                        borderTop: "none",
                        backgroundColor: colors.blueAccent[700],
                    },
                    "& .MuiCheckbox-root": {
                        color: `${colors.greenAccent[200]} !important`,
                    },
                    "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
                        color: `${colors.grey[100]} !important`,
                        fontSize: "13px !important",
                    },
                }), [colors])}
            >
                <UserDataGrid data={usersWithRoles} columns={columns} />
            </Box>

            <Dialog
                fullScreen={fullScreen}
                open={openView2}
                onClose={handleCloseView2}
                aria-labelledby="responsive-dialog-title"
                maxWidth={"sm"}
                fullWidth
            >
                <DialogTitle id="responsive-dialog-title" style={{ backgroundColor: theme.palette.background.purple, textAlign: "center", fontWeight: 'bold', fontSize: '18px' }}>
                    {"User Details"}
                </DialogTitle>
                <DialogContent style={{ backgroundColor: theme.palette.background.purple }}>
                    <DialogContentText>
                        <div>
                            <p style={{
                                backgroundColor: theme.palette.background.light,
                                marginBottom: '2px',
                                padding: '10px',
                                display: 'flex',
                                alignItems: 'center'
                            }}>
                                <strong>User Profile : </strong>
                                {viewData?.avatar ? (
                                    <img
                                        src={`${imgUrl}/${viewData.avatar}`}
                                        alt="avatar"
                                        style={{
                                            width: '50px',
                                            height: '50px',
                                            objectFit: 'cover',
                                            marginLeft: '10px',
                                            borderRadius: '4px'
                                        }}
                                        onError={(e) => {
                                            e.target.onerror = null;
                                            e.target.src = '';
                                        }}
                                    />
                                ) : (
                                    <span>No Image Found</span>
                                )}
                            </p>
                            <p style={{
                                backgroundColor: theme.palette.background.light,
                                marginBottom: '2px',
                                padding: '10px'
                            }}>
                                <strong>Name : </strong>
                                <span style={{ wordBreak: 'break-word' }}>{viewData?.name}</span>
                            </p>
                            <p style={{
                                backgroundColor: theme.palette.background.light,
                                marginBottom: '2px',
                                padding: '10px'
                            }}>
                                <strong>Email : </strong>
                                <span style={{ wordBreak: 'break-word' }}>{viewData?.email}</span>
                            </p>
                            <p style={{
                                backgroundColor: theme.palette.background.light,
                                marginBottom: '2px',
                                padding: '10px'
                            }}>
                                <strong>Mobile No : </strong>
                                <span style={{ wordBreak: 'break-word' }}>{viewData?.contact}</span>
                            </p>
                            <p style={{
                                backgroundColor: theme.palette.background.light,
                                marginBottom: '2px',
                                padding: '10px'
                            }}>
                                <strong>Address : </strong>
                                <span style={{ wordBreak: 'break-word' }}>{viewData?.address}</span>
                            </p>
                            <p style={{
                                backgroundColor: theme.palette.background.light,
                                marginBottom: '2px',
                                padding: '10px'
                            }}>
                                <strong>Role : </strong>
                                {viewData?.roleData?.[0]?.roleName}
                            </p>
                            <p style={{
                                backgroundColor: theme.palette.background.light,
                                marginBottom: '2px',
                                padding: '10px'
                            }}>
                                <strong>Permissions : </strong>
                                {viewData?.roleData?.[0]?.permissions.join(' , ')}
                            </p>
                        </div>
                    </DialogContentText>
                </DialogContent>
                <DialogActions style={{ backgroundColor: theme.palette.background.purple }}>
                    <Button autoFocus onClick={handleCloseView2} style={{ color: theme.palette.text.primary }}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                fullScreen={fullScreen}
                open={openView}
                onClose={handleCloseView}
                aria-labelledby="responsive-dialog-title"
                maxWidth={"sm"}
                fullWidth
            >
                <DialogTitle id="responsive-dialog-title" style={{ backgroundColor: theme.palette.background.purple, textAlign: 'right' }}>
                    <Button
                        onClick={handleDownload}
                        style={{ color: theme.palette.text.primary }}
                        startIcon={<DownloadIcon />}
                        disabled={!viewImage}
                    >
                        Download
                    </Button>
                </DialogTitle>
                <DialogContent style={{ backgroundColor: theme.palette.background.purple, display: 'flex', justifyContent: 'center', alignItems: 'center' }} >
                    {viewImage ? (
                        <div>
                            <img
                                src={viewImage}
                                alt="Uploaded Image"
                                style={{ width: '600px', height: 'auto', objectFit: 'contain' }}
                                ref={downloadRef}
                            />
                        </div>
                    ) : (
                        <p>No image to display</p>
                    )}
                </DialogContent>
                <DialogActions style={{ backgroundColor: theme.palette.background.purple }}>
                    <Button autoFocus onClick={handleCloseView} style={{ color: theme.palette.text.primary }}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={deleteConfirmOpen} onClose={cancelDelete}>
                <DialogContent>
                    Are you sure you want to delete this user?
                </DialogContent>
                <DialogActions>
                    <Button onClick={cancelDelete} style={{ color: theme.palette.text.primary }}>No</Button>
                    <Button onClick={confirmDelete} style={{ color: theme.palette.text.primary }}>Yes</Button>
                </DialogActions>
            </Dialog>


        </Box>
    );
};

export default User;
